import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import './i18n';
import './sass/bootstrap-custom.scss';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { ConfigProvider } from './providers/ConfigProvider.tsx'
import { captureDevLoginFromUrl } from './helpers/devLogin'
import { purgeExpiredOidcUser, reloadOnLoginChangeInOtherTab } from './helpers/oidcStorage'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fal } from '@fortawesome/pro-light-svg-icons'
import { faCheck, faSparkles, faUpload, faXmark as faXmarkSolid } from '@fortawesome/pro-solid-svg-icons'
import {
    faArrowLeft, faArrowRight, faArrowRightFromBracket, faChevronLeft, faCircleCheck,
    faCircleExclamation, faCircleHalfStroke, faCircleMinus, faCircleXmark, faFilePdf,
    faFileZipper, faPencil, faPrint, faStarHalfStroke, faTimesCircle, faXmark
} from '@fortawesome/pro-regular-svg-icons'
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons'


const isDev = import.meta.env.DEV;
// Import custom CSS from external URL in development
// This is fetched automatically in production so remember to comment out before building
if (isDev) {
    import('./css/bootstrap-custom.min.css');
    import('./css/app.custom.css');

}

/**
 * Bare light (fal) registreres i sin helhet: ikonnavnene i sidemenyen
 * (Section/ReportSection), modalknappene og Input/AutoComplete kommer fra
 * skjemakonfigen og kan være hva som helst.
 *
 * Solid, regular og duotone brukes bare med faste navn i koden, så de
 * registreres ikon for ikon — hele settene gjorde bundlen og kildekartene så
 * store at CI-bygget gikk tom for minne. Bruker du et nytt ikon med
 * ['fas'|'far'|'fad', …], legg det til her; ellers rendres det ikke
 * (FontAwesome logger «Could not find icon» i konsollen).
 */
library.add(
    fal,
    faCheck, faSparkles, faUpload, faXmarkSolid,
    faArrowLeft, faArrowRight, faArrowRightFromBracket, faChevronLeft, faCircleCheck,
    faCircleExclamation, faCircleHalfStroke, faCircleMinus, faCircleXmark, faFilePdf,
    faFileZipper, faPencil, faPrint, faStarHalfStroke, faTimesCircle, faXmark,
    faSpinnerThird
)

if (!isDev) {
    Sentry.init({
        dsn: "https://e59fb4102f014ee5ab4da59c257319ec@o4506546797936640.ingest.us.sentry.io/4512213722660864",
        release: __APP_BUILD_ID__,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration()
        ],
        tracesSampleRate: 1.0,
        // sentry-trace/baggage legges på kall hit. API-ets CORS må tillate dem:
        // /rest/ gjør det i Rest.php, /app-api/ i MODX (verifisert 2026-10-07).
        tracePropagationTargets: [/^https:\/\/api\.lyfti\.no/],
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0
    });
}

/**
 * Etter en ny deploy er de gamle, hash-navngitte chunk-filene borte. En bruker
 * som allerede har appen åpen får da 404 når en lazy-lastet modul skal hentes.
 * Vite varsler dette som `vite:preloadError`; vi laster siden på nytt én gang.
 * Tidsstempelet i sessionStorage hindrer evig reload-løkke. Samme som lyfti-admin.
 *
 * Vite sender samme event for enhver feilet dynamisk import, og en reload
 * midt i et skjema koster søkeren det hen har skrevet. Peker feilmeldingen på
 * en URL utenfor appens egne /assets/, gjør vi derfor ingenting. Safari tar
 * ikke med URL-en i meldingen — da reloades det som før.
 */
const RELOAD_MARKER = 'Lyfti.chunkReloadedAt';

window.addEventListener('vite:preloadError', (event) => {
    const message = String(event.payload?.message ?? '');
    const isForeign = /https?:\/\//.test(message) && !message.includes(`${window.location.origin}/assets/`);
    if (isForeign) return;

    const lastReload = Number(sessionStorage.getItem(RELOAD_MARKER) ?? 0);
    if (Date.now() - lastReload < 10000) return;

    event.preventDefault();
    sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
    window.location.reload();
});

// Før noe leser innloggingen: kast en utløpt en, og følg med på inn-/utlogging
// i andre faner. Se helpers/oidcStorage.ts.
purgeExpiredOidcUser();
reloadOnLoginChangeInOtherTab();

// Dev-innloggingen må legge OIDC-brukeren i lageret før OidcAuthProvider
// mounter. I prod returnerer den med en gang.
captureDevLoginFromUrl().finally(() => {
    createRoot(document.getElementById('root')!).render(
        <ConfigProvider>
            <App />
        </ConfigProvider>
    )
})